/**
 * Single source of truth for Demo PC setup instructions.
 *
 * Update this file — DemoSetupBanner, all page banners, and the auto-inserted
 * assembly order note all derive from these constants automatically.
 */

import React from 'react';

export const DEMO_ADMIN_PASSWORD = 'AOIAdm1n2026!';
export const DEMO_PS1_URL        = '/AOI-Demo-Setup.ps1';

// ── Canonical setup steps ─────────────────────────────────────────────────────
//
// DEMO_SETUP_STEPS_JSX  — used by DemoSetupBanner (rich text, monospace filenames)
// DEMO_SETUP_STEPS_TEXT — used by the plain-text auto-inserted note

export const DEMO_SETUP_STEPS_JSX: React.ReactNode[] = [
  <>Download <span className="font-mono font-medium">AOI-Demo-Setup.zip</span> from the dashboard, extract it, then double-click <span className="font-mono font-medium">AOI-Demo-Setup.bat</span> — or right-click <span className="font-mono font-medium">AOI-Demo-Setup.ps1</span> → <span className="font-medium">Run as Administrator</span></>,
  <>Window stays open — note the <span className="font-mono font-medium">AOIAdmin</span> password printed at the bottom (<span className="font-mono font-medium">AOIDemo</span> has no password)</>,
  <>Restart the PC — it will auto-login as <span className="font-mono font-medium">AOIDemo</span> and the AOI Demo Agent will start automatically</>,
  <>Enter the station name and shop location in the agent setup window</>,
  <>Station appears in the dashboard within 30 s — assign this build and the HUD pushes automatically</>,
];

export const DEMO_SETUP_STEPS_TEXT = [
  'Download AOI-Demo-Setup.zip from the dashboard, extract it, then double-click AOI-Demo-Setup.bat — or right-click AOI-Demo-Setup.ps1 → Run as Administrator',
  'Window stays open — note the AOIAdmin password printed at the bottom (AOIDemo has no password)',
  'Restart the PC — it will auto-login as AOIDemo and the AOI Demo Agent will start automatically',
  'Enter the station name and shop location in the agent setup window',
  'Station appears in the dashboard within 30 s — assign this build and the HUD pushes automatically',
];

// ── Plain-text note auto-inserted on Demo assembly order creation ──────────────

export const DEMO_SETUP_NOTE = [
  'DEMO PC SETUP CHECKLIST',
  '─────────────────────────────────────────',
  '',
  ...DEMO_SETUP_STEPS_TEXT.map((s, i) => `${i + 1}. ${s}`),
  '',
  'Scripts available from Dashboard > Demo Stations > Download',
  '',
  '─────────────────────────────────────────',
  'Kiosk overrides (timed unlock via dashboard):',
  '  - USB access:     More Actions > Unlock USB',
  '  - Task Manager:   More Actions > Unlock Task Mgr',
  'Both re-lock automatically after the selected duration.',
].join('\n');

// ── Shared banner component ────────────────────────────────────────────────────

interface DemoSetupBannerProps {
  /** Use compact padding/sizing for tight spaces (e.g. order card sidebars). Default false. */
  compact?: boolean;
  /** Show the PS1 download link at the bottom. Default true. */
  showDownload?: boolean;
}

export function DemoSetupBanner({ compact = false, showDownload = true }: DemoSetupBannerProps) {
  return (
    <div className={`rounded-md border border-blue-300 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 ${compact ? 'p-3 space-y-1.5' : 'p-4 space-y-2'}`}>
      <p className={`font-semibold text-blue-800 dark:text-blue-300 ${compact ? 'text-xs' : 'text-sm'}`}>
        Demo PC — setup required before handover
      </p>
      <ol className="text-xs text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
        {DEMO_SETUP_STEPS_JSX.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
      {showDownload && (
        <p className="text-xs text-blue-600 dark:text-blue-400">
          Scripts available from <span className="font-medium">Dashboard → Demo Stations → Downloads</span>
        </p>
      )}
    </div>
  );
}
